import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'application/store';

import { AccountState } from './types';
import { ACCOUNT_FETCH_CONFIG } from './consts';

export const selectAccountState = (state: RootState): AccountState => state.account;

export const selectLastFetchTime = (state: RootState) => state.account.lastFetchTime;

export const selectAccountLoading = createSelector([selectAccountState], (account) => account.loading);

export const selectAccountError = createSelector([selectAccountState], (account) => account.error);

export const selectUserId = createSelector([selectAccountState], (account) => account.userId);

export const selectCurrentCompanyId = createSelector([selectAccountState], (account) => account.currentCompanyId);

export const selectCompanyIds = createSelector([selectAccountState], (account) => account.companyIds);

export const selectStreamLibraryId = createSelector([selectAccountState], (account) => account.streamLibraryId);

export const selectIsBillingEnabled = createSelector([selectAccountState], (account) => account.isBillingEnabled);

export const selectIsReadyToUseLK = createSelector([selectAccountState], (account) => account.isReadyToUseLK);

export const selectIsAuthorized = createSelector([selectAccountState], (account) => account.isAuthorized);

export const selectIsWithoutCompanies = createSelector([selectAccountState], (account) => account.isWithoutCompanies);

export const selectAccountDataStale = createSelector([selectAccountState], (account) => account.dataStale);

export const selectAccountStatus = createSelector(
  [selectAccountLoading, selectAccountError, selectIsAuthorized],
  (loading, error, isAuthorized) => ({
    loading,
    error,
    isAuthorized,
    hasError: !!error,
    isIdle: !loading && !error && !isAuthorized,
  })
);

export const selectShouldRefreshData = createSelector([selectAccountState], (account) => {
  if (account.dataStale) return true;

  if (!account.lastFetchTime) return true;

  const timeSinceLastFetch = Date.now() - account.lastFetchTime;

  return timeSinceLastFetch > ACCOUNT_FETCH_CONFIG.staleTime;
});

export const selectAccountData = createSelector(
  [
    selectUserId,
    selectCurrentCompanyId,
    selectCompanyIds,
    selectIsReadyToUseLK,
    selectIsAuthorized,
    selectAccountLoading,
    selectAccountError,
  ],
  (userId, currentCompanyId, companyIds, isReadyToUseLK, isAuthorized, loading, error) => ({
    userId,
    currentCompanyId,
    companyIds,
    isReadyToUseLK,
    isAuthorized,
    loading,
    error,
  })
);
